import { useMutation, useQuery } from '@tanstack/react-query'
import Card from 'react-bootstrap/Card'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import {
  calGrade,
  fetchEducationHistory,
  EduInfosSchema,
  DEFAULT_EDU_INFO,
  syncEducationHistory,
  syncProgress,
} from './data'
import type { EducationHistoryUpdate } from './data'
import { useEffect, useState } from 'react'
import { readOnlyMode } from './style.css'
import { GraduationCapIcon } from 'lucide-react'
import { SquarePen } from 'lucide-react'
import type { AreaPrefectureMapping, School } from '@/util/ref'
import { fetchAreaPrefecturesMap, fetchSchools } from '@/util/ref'
import { cursorPointer } from '../style.css'
import { useForm, useWatch, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { range } from '@/util'
import { useTranslation } from 'react-i18next'
import { fetchProgramTypes, type ProgramType } from '@/util/ref'
import { queryClient } from '@/util/queryClient'
import { ProgressFlags } from '@/util/progress'
import Button from 'react-bootstrap/esm/Button'
import Stack from 'react-bootstrap/esm/Stack'

const DEFAULT_DURATION: Record<string, number> = {
  KOSEN_REGULAR: 5,
  KOSEN_ADVANCED: 2,
}

function getDefaultDuration(programType?: string) {
  return programType ? DEFAULT_DURATION[programType] || 0 : 0
}

export default function EduHistoryPanel() {
  const { t } = useTranslation()
  const { data: programTypes } = useQuery<ProgramType[]>({
    queryKey: ['program-type'],
    queryFn: fetchProgramTypes,
    staleTime: 1000 * 60 * 5,
  })
  const { data: educationHistories } = useQuery({
    queryKey: ['education-history'],
    queryFn: fetchEducationHistory,
    staleTime: 1000 * 60 * 60,
  })

  const { data: areaPrefectureMapping } = useQuery<AreaPrefectureMapping>({
    queryKey: ['area-prefecture'],
    queryFn: fetchAreaPrefecturesMap,
    staleTime: Infinity,
  })

  const { data: schoolProgramList } = useQuery<School[]>({
    queryKey: ['school'],
    queryFn: fetchSchools,
    staleTime: 1000 * 60 * 5,
  })

  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(EduInfosSchema),
    defaultValues: {
      edus: (educationHistories || []).map((e) => ({
        programType: e.programType || DEFAULT_EDU_INFO.programType,
        region: e.school?.subnationalUnit || DEFAULT_EDU_INFO.region,
        prefecture: e.school?.adminDivision || DEFAULT_EDU_INFO.prefecture,
        schoolId: e.school?.id || DEFAULT_EDU_INFO.schoolId,
        academicProgramId: e.academicProgram?.id || DEFAULT_EDU_INFO.academicProgramId,
        academicCourseId: e.academicCourse?.id || DEFAULT_EDU_INFO.academicCourseId,
        grade: calGrade(e.enrollmentStartDate),
      })),
    },
  })

  const [editMode, setEditMode] = useState<boolean>(false)

  const eduHistoryMutation = useMutation({
    mutationFn: (edus: EducationHistoryUpdate[]) => syncEducationHistory(edus),
    onSuccess: () => {
      setEditMode(false)
      queryClient.invalidateQueries({ queryKey: ['education-history'] })
      syncProgress(ProgressFlags.EDU_HISTORY_FILLED)
    },
    onError: (err) => console.log(err),
  })

  const edus = useWatch({ control, name: 'edus' })

  useEffect(() => {
    if (!educationHistories) return
    reset({
      edus: (educationHistories || []).map((e) => {
        const courses = schoolProgramList
          ?.find((s) => s.id === e.school?.id)
          ?.programs.find((p) => p.id === e.academicProgram?.id)?.courses
        return {
          programType: e.programType || DEFAULT_EDU_INFO.programType,
          region: e.school?.subnationalUnit || DEFAULT_EDU_INFO.region,
          prefecture: e.school?.adminDivision || DEFAULT_EDU_INFO.prefecture,
          schoolId: e.school?.id || DEFAULT_EDU_INFO.schoolId,
          academicProgramId: e.academicProgram?.id || DEFAULT_EDU_INFO.academicProgramId,
          academicCourseId: courses?.length
            ? e.academicCourse?.id || DEFAULT_EDU_INFO.academicCourseId
            : 0,
          grade: calGrade(e.enrollmentStartDate),
        }
      }),
    })
  }, [educationHistories, reset, schoolProgramList])

  return (
    <>
      {edus?.map((edu, idx) => (
        <Card key={idx}>
          <Card.Body>
            <Card.Title className="d-flex flex-row justify-content-between align-items-center">
              <div>
                <GraduationCapIcon className="me-1" />
                学校情報
              </div>
              {!editMode && (
                <SquarePen onClick={() => setEditMode(true)} className={cursorPointer} />
              )}
            </Card.Title>
            {editMode && (
              <>
                <FloatingLabel controlId="program-type-selector" label="学科種別" className="mb-3">
                  <Controller
                    name={`edus.${idx}.programType`}
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        isInvalid={errors.edus && !!errors.edus[idx]?.programType}
                        onChange={(e) => {
                          field.onChange(e)
                          setValue(`edus.${idx}.schoolId`, DEFAULT_EDU_INFO.schoolId)
                          setValue(
                            `edus.${idx}.academicProgramId`,
                            DEFAULT_EDU_INFO.academicProgramId
                          )
                          setValue(
                            `edus.${idx}.academicCourseId`,
                            DEFAULT_EDU_INFO.academicCourseId
                          )
                          setValue(`edus.${idx}.grade`, DEFAULT_EDU_INFO.grade)
                        }}
                      >
                        {(programTypes || []).map((pt) => (
                          <option key={pt.id} value={pt.id}>
                            {t(`programType.${pt.id}`)}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                </FloatingLabel>
                <FloatingLabel label="地方" className="mb-3">
                  <Controller
                    name={`edus.${idx}.region`}
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        isInvalid={errors.edus && !!errors.edus[idx]?.region}
                        onChange={(e) => {
                          field.onChange(e)
                          const courses = schoolProgramList
                            ?.find((s) => s.id === edu.schoolId)
                            ?.programs.find((p) => p.id === edu.academicProgramId)?.courses
                          setValue(`edus.${idx}.prefecture`, DEFAULT_EDU_INFO.prefecture)
                          setValue(`edus.${idx}.schoolId`, DEFAULT_EDU_INFO.schoolId)
                          setValue(
                            `edus.${idx}.academicProgramId`,
                            DEFAULT_EDU_INFO.academicProgramId
                          )
                          setValue(
                            `edus.${idx}.academicCourseId`,
                            edu.academicProgramId === 0
                              ? 0
                              : courses?.length
                                ? DEFAULT_EDU_INFO.academicCourseId
                                : 0
                          )
                        }}
                      >
                        {areaPrefectureMapping &&
                          Object.keys(areaPrefectureMapping.areaToPrefecture).map((k) => (
                            <option key={k} value={k}>
                              {t(`region.${k}`)}
                            </option>
                          ))}
                      </Form.Select>
                    )}
                  />
                </FloatingLabel>
                <FloatingLabel label="都道府県" className="mb-3">
                  <Controller
                    name={`edus.${idx}.prefecture`}
                    control={control}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        isInvalid={errors.edus && !!errors.edus[idx]?.prefecture}
                        onChange={(e) => {
                          field.onChange(e)
                          const courses = schoolProgramList
                            ?.find((s) => s.id === edu.schoolId)
                            ?.programs.find((p) => p.id === edu.academicProgramId)?.courses
                          setValue(`edus.${idx}.schoolId`, DEFAULT_EDU_INFO.schoolId)
                          setValue(
                            `edus.${idx}.academicProgramId`,
                            DEFAULT_EDU_INFO.academicProgramId
                          )
                          setValue(
                            `edus.${idx}.academicCourseId`,
                            edu.academicProgramId === 0
                              ? 0
                              : courses?.length
                                ? DEFAULT_EDU_INFO.academicCourseId
                                : 0
                          )
                        }}
                      >
                        <option value={DEFAULT_EDU_INFO.prefecture} disabled hidden>
                          都道府県を選択してください
                        </option>
                        {(
                          areaPrefectureMapping?.areaToPrefecture?.[edu.region || ''] ??
                          Object.keys(areaPrefectureMapping?.prefectureToArea ?? {})
                        ).map((k) => (
                          <option key={k} value={k}>
                            {t(`prefecture.${k}`)}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                </FloatingLabel>
              </>
            )}
            <FloatingLabel label="学校名" className="mb-3">
              <Controller
                name={`edus.${idx}.schoolId`}
                control={control}
                render={({ field }) => (
                  <>
                    {editMode ? (
                      <Form.Select
                        {...field}
                        isInvalid={errors.edus && !!errors.edus[idx]?.schoolId}
                        onChange={(e) => {
                          field.onChange(Number(e.target.value))
                          const courses = schoolProgramList
                            ?.find((s) => s.id === edu.schoolId)
                            ?.programs.find((p) => p.id === edu.academicProgramId)?.courses
                          setValue(
                            `edus.${idx}.academicProgramId`,
                            DEFAULT_EDU_INFO.academicProgramId
                          )
                          setValue(
                            `edus.${idx}.academicCourseId`,
                            edu.academicProgramId === 0
                              ? 0
                              : courses?.length
                                ? DEFAULT_EDU_INFO.academicCourseId
                                : 0
                          )
                        }}
                      >
                        <option value={DEFAULT_EDU_INFO.schoolId} disabled hidden>
                          学校を選択してください
                        </option>
                        {schoolProgramList
                          ?.filter(
                            (s) =>
                              s.subnationalUnit === edu.region &&
                              s.adminDivision === edu.prefecture &&
                              s.programs.map((p) => p.programType).includes(edu.programType || '')
                          )
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        value={educationHistories && educationHistories[idx].school?.name}
                        className={readOnlyMode}
                        readOnly
                      />
                    )}
                  </>
                )}
              />
            </FloatingLabel>
            <FloatingLabel label="学科" className="mb-3">
              <Controller
                name={`edus.${idx}.academicProgramId`}
                control={control}
                render={({ field }) => (
                  <>
                    {editMode ? (
                      <Form.Select
                        {...field}
                        isInvalid={errors.edus && !!errors.edus[idx]?.academicProgramId}
                        onChange={(e) => {
                          const academicProgramId = Number(e.target.value)
                          field.onChange(academicProgramId)
                          const courses = schoolProgramList
                            ?.find((s) => s.id === edu.schoolId)
                            ?.programs.find((p) => p.id === academicProgramId)?.courses
                          setValue(
                            `edus.${idx}.academicCourseId`,
                            academicProgramId === 0
                              ? 0
                              : courses?.length
                                ? DEFAULT_EDU_INFO.academicCourseId
                                : 0
                          )
                        }}
                      >
                        <option value={DEFAULT_EDU_INFO.academicProgramId} disabled hidden>
                          学科選択してください
                        </option>
                        {schoolProgramList
                          ?.find((s) => s.id === edu.schoolId)
                          ?.programs.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        <option value={0}>その他</option>
                      </Form.Select>
                    ) : (
                      <Form.Control
                        value={
                          (educationHistories && educationHistories[idx].academicProgram?.name) ||
                          'その他'
                        }
                        readOnly
                        className={readOnlyMode}
                      />
                    )}
                  </>
                )}
              />
            </FloatingLabel>
            <Controller
              name={`edus.${idx}.academicCourseId`}
              control={control}
              render={({ field }) => {
                const courses = schoolProgramList
                  ?.find((s) => s.id === edu.schoolId)
                  ?.programs.find((p) => p.id === edu.academicProgramId)?.courses
                if (!courses?.length) return <></>
                return editMode ? (
                  <FloatingLabel label="コース" className="mb-3">
                    <Form.Select
                      {...field}
                      isInvalid={errors.edus && !!errors.edus[idx]?.academicCourseId}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={DEFAULT_EDU_INFO.academicCourseId} disabled hidden>
                        コース選択してください
                      </option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                      <option value={0}>専攻なし</option>
                    </Form.Select>
                  </FloatingLabel>
                ) : field.value && field.value > 0 ? (
                  <FloatingLabel label="コース" className="mb-3">
                    <Form.Control
                      value={educationHistories && educationHistories[idx].academicCourse?.name}
                      readOnly
                      className={readOnlyMode}
                    />
                  </FloatingLabel>
                ) : (
                  <></>
                )
              }}
            />
            <FloatingLabel label="学年" className="mb-3">
              <Controller
                name={`edus.${idx}.grade`}
                control={control}
                render={({ field }) => {
                  const durationYears =
                    schoolProgramList
                      ?.find((s) => s.id === edu.schoolId)
                      ?.programs.find((p) => p.id === edu.academicProgramId)?.durationYears ||
                    getDefaultDuration(edu.programType)
                  return editMode ? (
                    <Form.Select
                      {...field}
                      isInvalid={errors.edus && !!errors.edus[idx]?.grade}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={DEFAULT_EDU_INFO.grade} disabled hidden>
                        学年選択してください
                      </option>
                      {Array.from(range(1, durationYears + 1)).map((i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control value={field.value} readOnly className={readOnlyMode} />
                  )
                }}
              />
            </FloatingLabel>
          </Card.Body>
          {editMode && (
            <Card.Footer>
              <Stack direction="horizontal" gap={3} className="justify-content-end">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    reset({
                      edus: (educationHistories || []).map((e) => ({
                        programType: e.programType || DEFAULT_EDU_INFO.programType,
                        region: e.school?.subnationalUnit || DEFAULT_EDU_INFO.region,
                        prefecture: e.school?.adminDivision || DEFAULT_EDU_INFO.prefecture,
                        schoolId: e.school?.id || DEFAULT_EDU_INFO.schoolId,
                        academicProgramId:
                          e.academicProgram?.id || DEFAULT_EDU_INFO.academicProgramId,
                        academicCourseId: e.academicCourse?.id || DEFAULT_EDU_INFO.academicCourseId,
                        grade: calGrade(e.enrollmentStartDate),
                      })),
                    })
                    setEditMode(false)
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={handleSubmit(
                    ({ edus }) => eduHistoryMutation.mutate(edus),
                    (err) => console.log(err)
                  )}
                >
                  保存
                </Button>
              </Stack>
            </Card.Footer>
          )}
        </Card>
      ))}
    </>
  )
}
